import {
  CollectType,
  type SiteJobDescriptionPublicDetailResponse,
} from '@flex-apis/recruiting';

export interface AdditionalInfoCollect {
  /** 지원 화면에 해당 항목을 노출하는지. */
  readonly visible: boolean;
  /** 노출 시 필수 입력인지 (false = 선택). */
  readonly required: boolean;
}

export type AdditionalInfo = Readonly<
  Record<
    'education' | 'work' | 'militaryService' | 'veteran' | 'handicapCase',
    AdditionalInfoCollect
  >
>;

export interface ResolveAdditionalInfoCollectParams {
  /** 신계약(additionalInfoSettings) 값. undefined = 키 없음(미수집 또는 backfill 안 됨). */
  readonly collectType: CollectType | undefined;
  /** 구계약(required*) 수집 여부. dual-write fallback 에만 사용. */
  readonly legacyCollected: boolean;
  /** 민감정보(보훈·장애) 여부. 민감정보는 정책상 필수 불가(선택만). */
  readonly sensitive: boolean;
}

/**
 * 추가정보 항목의 노출/필수 여부를 결정하는 계약.
 *
 * 신계약 `additionalInfoSettings`(REQUIRED/OPTIONAL)를 우선한다. 키가 없으면 dual-write
 * 기간 동안 구계약 `required*` 로 fallback 한다.
 */
export function resolveAdditionalInfoCollect(
  params: ResolveAdditionalInfoCollectParams
): AdditionalInfoCollect {
  if (params.collectType === CollectType.Required) {
    return { visible: true, required: true };
  }
  if (params.collectType === CollectType.Optional) {
    return { visible: true, required: false };
  }

  // NOTE: dual-write fallback. BE 가 additionalInfoSettings 단일 소스로 전환(구 required* 제거)되면
  // 이 분기와 resolveLegacyCollect 를, 그리고 테스트의 'dual-write fallback' describe 를 함께 제거한다.
  return resolveLegacyCollect(params);
}

function resolveLegacyCollect(
  params: ResolveAdditionalInfoCollectParams
): AdditionalInfoCollect {
  if (!params.legacyCollected) {
    return { visible: false, required: false };
  }
  // 구계약은 필수/선택을 구분하지 못한다 → 민감정보는 선택, 그 외는 필수로 간주.
  return { visible: true, required: !params.sensitive };
}

/**
 * parseAdditionalInfo 의 입력. 생성된 응답 타입과 달리 additionalInfoSettings 를 optional 로 둔다 —
 * dual-write 기간 동안 BE 가 아직 내려주지 않을 수 있기 때문이다 (CI-5028).
 */
export interface ParseAdditionalInfoParams {
  readonly additionalInfoSettings?: SiteJobDescriptionPublicDetailResponse['additionalInfoSettings'];
  readonly requiredEducation: boolean;
  readonly requiredWork: boolean;
  readonly requiredMilitaryService: boolean;
  readonly requiredVeteran: boolean;
  readonly requiredHandicapCase: boolean;
}

export function parseAdditionalInfo(
  response: ParseAdditionalInfoParams
): AdditionalInfo {
  const settings = response.additionalInfoSettings ?? {};

  return {
    education: resolveAdditionalInfoCollect({
      collectType: settings['EDUCATION'],
      legacyCollected: response.requiredEducation,
      sensitive: false,
    }),
    work: resolveAdditionalInfoCollect({
      collectType: settings['WORK'],
      legacyCollected: response.requiredWork,
      sensitive: false,
    }),
    militaryService: resolveAdditionalInfoCollect({
      collectType: settings['MILITARY_SERVICE'],
      legacyCollected: response.requiredMilitaryService,
      sensitive: false,
    }),
    veteran: resolveAdditionalInfoCollect({
      collectType: settings['VETERAN'],
      legacyCollected: response.requiredVeteran,
      sensitive: true,
    }),
    handicapCase: resolveAdditionalInfoCollect({
      collectType: settings['HANDICAP_CASE'],
      legacyCollected: response.requiredHandicapCase,
      sensitive: true,
    }),
  };
}
